import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  Header,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from '@cloudscape-design/components';
import { useNavigate, useParams } from 'react-router-dom';
import { CAMPAIGN_STATUS_INDICATOR, CampaignCsvUpload, useCampaign } from '@features/campaigns';

/**
 * Individual campaign page. Shows campaign details and the CSV upload panel.
 * Route: /campaigns/:campaignId
 */
export function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { campaign, isLoading, error } = useCampaign(campaignId);

  if (isLoading) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  if (error || !campaign) {
    return (
      <ContentLayout header={<Header variant="h1">Campaign not found</Header>}>
        <Alert
          type="error"
          header="We couldn't find that campaign"
          action={<Button onClick={() => navigate('/campaigns')}>Back to campaigns</Button>}
        >
          The campaign you followed no longer exists, or the link was mistyped.
        </Alert>
      </ContentLayout>
    );
  }

  const status = CAMPAIGN_STATUS_INDICATOR[campaign.status];

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={campaign.description}>
          {campaign.campaignName}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Details</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Campaign ID</Box>
              <div>{campaign.campaignId}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Upload destination</Box>
              <div>{campaign.target}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Status</Box>
              <div>
                <StatusIndicator type={status.type}>{status.label}</StatusIndicator>
              </div>
            </div>
          </ColumnLayout>
        </Container>

        <CampaignCsvUpload
          campaignId={campaign.campaignId}
          campaignName={campaign.campaignName}
          target={campaign.target}
          disabled={campaign.status !== 'active'}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}
